import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../utils/AuthContext';

import {
  Select, Box, Input, Button, VStack, Heading, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Text, RadioGroup, Radio, HStack,
} from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const PaperSetsPage = () => {
  const [title, setTitle] = useState('');
  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [paperSets, setPaperSets] = useState([]);
  const [editingPaperSet, setEditingPaperSet] = useState(null);
  const [filterIsActive, setFilterIsActive] = useState('all');
  const { auth } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userEmail = auth?.username;

  useEffect(() => {
    const fetchPaperSets = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/paperset`, {
          headers: {
            Authorization: `Bearer ${auth.token}`
          }
        });
        const data = response.data.$values || response.data;
        setPaperSets(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching paper sets:', error.response ? error.response.data : error.message);
        setPaperSets([]);
      }
    };

    fetchPaperSets();
  }, [auth.token]);

  const handleCreatePaperSet = async (e) => {
    e.preventDefault();

    if (numberOfQuestions <= 0) {
      alert("Number of questions must be greater than zero.");
      return;
    }

    const paperSet = {
      title,
      numberOfQuestions,
      createdDate: new Date().toISOString(),
      isActive,
      questions: []
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/paperset`, paperSet, {
        headers: {
          Authorization: `Bearer ${auth.token}`
        }
      });
      alert('Paper set created successfully');
      setTitle('');
      setNumberOfQuestions(0);
      setIsActive(true);
      setPaperSets([...paperSets, response.data]);
    } catch (error) {
      console.error('Error creating paper set:', error.response ? error.response.data : error.message);
      if (error.response && error.response.status === 409) {
        alert(error.response.data.message || 'A paper set with the same title already exists.');
      } else if (error.response && error.response.data) {
        alert(error.response.data.message || 'Failed to create paper set');
      } else {
        alert('Failed to create paper set');
      }
    }
  };

  const handleEdit = (paperSet) => {
    if (!paperSet.isActive && userEmail !== 'gsm@technomediaindia.com') {
      alert("Editing inactive paper sets is not allowed.");
      return;
    }
    setEditingPaperSet(paperSet);
    onOpen();
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: editingPaperSet.title,
        numberOfQuestions: editingPaperSet.numberOfQuestions,
        paperSetId: editingPaperSet.paperSetId,
        isActive: editingPaperSet.isActive,
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/paperset/${editingPaperSet.paperSetId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
            Authorization: `Bearer ${auth.token}`
          },
        }
      );

      setPaperSets(paperSets.map((ps) => (ps.paperSetId === editingPaperSet.paperSetId ? editingPaperSet : ps)));
      setEditingPaperSet(null);
      onClose();
    } catch (error) {
      console.error('Error updating paper set:', error.response ? error.response.data : error.message);
    }
  };

  const handleDelete = async (id) => {
    const paperSet = paperSets.find((ps) => ps.paperSetId === id);
    if (!paperSet) return;

    if (!paperSet.isActive) {
      alert('Deleting inactive paper sets is not allowed.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this paper set?')) return;

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/paperset/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`
        }
      });

      if (response.status === 204) {
        // No exam history: the paper set was physically deleted.
        setPaperSets((current) => current.filter((ps) => ps.paperSetId !== id));
        return;
      }

      // Existing exam history: backend intentionally deactivates instead of deleting.
      setPaperSets((current) =>
        current.map((ps) => (ps.paperSetId === id ? { ...ps, isActive: false } : ps))
      );

      const message =
        response.data?.message ||
        (typeof response.data === 'string' ? response.data : 'Paper set marked as inactive.');
      alert(message);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : null) ||
        error.message ||
        'Failed to delete paper set.';

      console.error('Error deleting paper set:', message, error);
      alert(message);
    }
  };

  const columnDefs = [
    { headerName: 'Title', field: 'title', flex: 1 },
    { headerName: 'Number of Questions', field: 'numberOfQuestions', width: 150 },
    {
      headerName: 'Created Date',
      field: 'createdDate',
      valueGetter: (params) => new Date(params.data.createdDate).toLocaleDateString(),
      width: 150
    },
    { headerName: 'Is Active', field: 'isActive', width: 100 },
    {
      headerName: 'Actions',
      cellRenderer: (params) => (
        <div>
          <Button size="sm" colorScheme="blue" onClick={() => handleEdit(params.data)} isDisabled={!params.data.isActive && userEmail !== 'gsm@technomediaindia.com'}>Edit</Button>
          <Button size="sm" colorScheme="red" ml={2} onClick={() => handleDelete(params.data.paperSetId)} isDisabled={!params.data.isActive}>Delete</Button>
        </div>
      ),
      width: 150,
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
  };

  const filteredPaperSets = paperSets.filter((ps) => {
    if (filterIsActive === 'true') return ps.isActive;
    if (filterIsActive === 'false') return !ps.isActive;
    return true; // if 'all' is selected
  });
  return (
    <Box p={8}>
      <Heading mb={6}>Manage Paper Sets</Heading>
      <Box mb={6} width="33%">
        <VStack spacing={4}>
          <Heading size="md">Create Paper Set</Heading>
          <Input
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            placeholder="Number of Questions"
            type="number"
            value={numberOfQuestions}
            onChange={(e) => setNumberOfQuestions(Number(e.target.value))}
          />
          <Select placeholder="Select Is Active" value={isActive} onChange={(e) => setIsActive(e.target.value === 'true')}>
            <option value="true">True</option>
            <option value="false">False</option>
          </Select>
          <Button colorScheme="blue" onClick={handleCreatePaperSet}>Create Paper Set</Button>
        </VStack>
      </Box>
      <RadioGroup value={filterIsActive} onChange={setFilterIsActive} mb={6}>
        <HStack spacing="24px">
          <Radio value="all">All</Radio>
          <Radio value="true">Active</Radio>
          <Radio value="false">Inactive</Radio>
        </HStack>
      </RadioGroup>
      <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
        <AgGridReact
          rowData={filteredPaperSets}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50, 100]}
        />
      </div>

      {editingPaperSet && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Paper Set</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Input
                  value={editingPaperSet.title}
                  onChange={(e) => setEditingPaperSet({ ...editingPaperSet, title: e.target.value })}
                  placeholder="Title"
                />
                <Input
                  value={editingPaperSet.numberOfQuestions}
                  onChange={(e) => setEditingPaperSet({ ...editingPaperSet, numberOfQuestions: Number(e.target.value) })}
                  placeholder="Number of Questions"
                  type="number"
                />
                <Select placeholder="Select Is Active" value={editingPaperSet.isActive} onChange={(e) => setEditingPaperSet({ ...editingPaperSet, isActive: e.target.value === 'true' })}>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Select>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSave}>
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default PaperSetsPage;
